import * as React from 'react';

import { getApiBaseUrl } from '../../lib/getEnv';

interface IHealthResponse {
  status: string;
  timestamp?: string;
  version?: string;
  [key: string]: string | undefined;
}

interface IKeyValueDisplayProps {
  data: Record<string, string | undefined>;
}

const KeyValueDisplay = ({ data }: IKeyValueDisplayProps) => {
  const statusValue = data.status;
  const isHealthy = statusValue === 'ok' || statusValue === 'healthy';

  return (
    <div className="divide-primary divide-y">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="flex items-start gap-4 p-4">
          <div className="text-primary min-w-[150px] font-semibold">{key}</div>
          <div
            className={`break-all font-mono text-sm ${
              key === 'status'
                ? `font-semibold ${isHealthy ? 'text-success-primary' : 'text-error-primary'}`
                : 'text-secondary'
            }`}
          >
            {value === undefined ? <span className="text-tertiary italic">(not set)</span> : value}
          </div>
        </div>
      ))}
    </div>
  );
};

const DebugEnvironmentPane = () => {
  const [healthData, setHealthData] = React.useState<Record<string, string | undefined> | null>(null);
  const [healthError, setHealthError] = React.useState<string | null>(null);
  const [healthLoading, setHealthLoading] = React.useState(true);

  const [dbHealthData, setDbHealthData] = React.useState<Record<string, string | undefined> | null>(null);
  const [dbHealthError, setDbHealthError] = React.useState<string | null>(null);
  const [dbHealthLoading, setDbHealthLoading] = React.useState(true);

  // Get all VITE_* environment variables. An empty `VITE_API_SERVER_URL` means
  // same-origin (CloudFront-hosted builds use relative URLs) — display it as
  // "(same-origin)" rather than the default "(not set)" placeholder so it's clear
  // this is intentional, not a misconfiguration.
  const viteEnvVars = React.useMemo(() => {
    const envVars: Record<string, string | undefined> = {};
    const env = import.meta.env;
    Object.keys(env).forEach((key) => {
      if (key.startsWith('VITE_')) {
        const value = env[key] as string | undefined;
        if (key === 'VITE_API_SERVER_URL' && value === '') {
          envVars[key] = '(same-origin)';
        } else {
          envVars[key] = value;
        }
      }
    });
    return envVars;
  }, []);

  // Fetch health endpoints. `getApiBaseUrl()` returns an empty string in
  // same-origin mode, in which case `${apiUrl}/health` becomes the relative
  // `/health` and the browser resolves it against the current origin.
  React.useEffect(() => {
    const apiUrl = getApiBaseUrl();

    const fetchHealth = async () => {
      try {
        setHealthLoading(true);
        setHealthError(null);

        const response = await fetch(`${apiUrl}/health`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Health check failed with status: ${response.status}`);
        }

        const data = (await response.json()) as IHealthResponse;
        // Convert all values to strings
        const stringData = Object.fromEntries(
          Object.entries(data).map(([k, v]) => [k, v == null ? undefined : String(v)])
        ) as Record<string, string | undefined>;
        setHealthData(stringData);
      } catch (error) {
        setHealthError(error instanceof Error ? error.message : 'Failed to fetch health endpoint');
      } finally {
        setHealthLoading(false);
      }
    };

    const fetchDbHealth = async () => {
      try {
        setDbHealthLoading(true);
        setDbHealthError(null);

        const response = await fetch(`${apiUrl}/db-health`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`DB health check failed with status: ${response.status}`);
        }

        const data = (await response.json()) as IHealthResponse;
        // Convert all values to strings
        const stringData = Object.fromEntries(
          Object.entries(data).map(([k, v]) => [k, v == null ? undefined : String(v)])
        ) as Record<string, string | undefined>;
        setDbHealthData(stringData);
      } catch (error) {
        setDbHealthError(error instanceof Error ? error.message : 'Failed to fetch db-health endpoint');
      } finally {
        setDbHealthLoading(false);
      }
    };

    void fetchHealth();
    void fetchDbHealth();
  }, []);

  return (
    <div className="flex h-full w-full flex-col gap-6 overflow-y-auto p-6">
      <div className="flex flex-col gap-4">
        <div className="text-3xl font-bold">Environment &amp; health check</div>
        <div className="text-secondary text-sm">View frontend environment variables and API server health status.</div>
      </div>

      <div className="flex flex-col gap-6">
        {/* Environment Variables Section */}
        <div className="flex flex-col gap-4">
          <div className="text-xl font-semibold">Environment variables (VITE_*)</div>
          <div className="border-primary rounded-lg border">
            {Object.keys(viteEnvVars).length === 0 ? (
              <div className="text-secondary p-4 text-sm">No VITE_* environment variables found</div>
            ) : (
              <KeyValueDisplay
                data={Object.fromEntries(Object.entries(viteEnvVars).sort(([a], [b]) => a.localeCompare(b)))}
              />
            )}
          </div>
        </div>

        {/* Health Check Section */}
        <div className="flex flex-col gap-4">
          <div className="text-xl font-semibold">API server health</div>
          <div className="border-primary rounded-lg border">
            {healthLoading ? (
              <div className="text-secondary p-4 text-sm">Loading health status...</div>
            ) : healthError ? (
              <div className="bg-error-primary text-error-primary p-4 text-sm">
                <div className="font-semibold">Error:</div>
                <div className="mt-1">{healthError}</div>
              </div>
            ) : healthData ? (
              <KeyValueDisplay data={healthData} />
            ) : (
              <div className="text-secondary p-4 text-sm">No health data available</div>
            )}
          </div>
        </div>

        {/* DB Health Check Section */}
        <div className="flex flex-col gap-4">
          <div className="text-xl font-semibold">Database health</div>
          <div className="border-primary rounded-lg border">
            {dbHealthLoading ? (
              <div className="text-secondary p-4 text-sm">Loading DB health status...</div>
            ) : dbHealthError ? (
              <div className="bg-error-primary text-error-primary p-4 text-sm">
                <div className="font-semibold">Error:</div>
                <div className="mt-1">{dbHealthError}</div>
              </div>
            ) : dbHealthData ? (
              <KeyValueDisplay data={dbHealthData} />
            ) : (
              <div className="text-secondary p-4 text-sm">No DB health data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebugEnvironmentPane;
